<script setup lang="ts">
import { useCommercialDocumentActions } from '@/composables/revenue/useCommercialDocumentActions';
import { useProjectQuickCreate } from '@/composables/projects/useProjectQuickCreate';
import { projectSettingsPath } from '~/lib/projectEditing';
import ClientWorkspaceDialogs from '~/components/clients/ClientWorkspaceDialogs.vue';
import ClientDeliveryTab from '@/components/clients/delivery/ClientDeliveryTab.vue';
import ClientPeopleTab from '@/components/clients/people/ClientPeopleTab.vue';
import ClientQuickLinksDialog from '@/components/clients/ClientQuickLinksDialog.vue';
import ClientContractDialogs from '@/components/clients/ClientContractDialogs.vue';
import ClientFinanceTab from '@/components/clients/finance/ClientFinanceTab.vue';
import ClientCommsTab from '@/components/clients/comms/ClientCommsTab.vue';
import ClientAutomationTab from '@/components/clients/automation/ClientAutomationTab.vue';
import ClientOverviewTab from '@/components/clients/overview/ClientOverviewTab.vue';
import ClientWorkspaceHero from '@/components/clients/hero/ClientWorkspaceHero.vue';
import WorkspaceActivitySheet from '@/components/workspace/WorkspaceActivitySheet.vue';
import WorkspaceDetailSheet from '@/components/workspace/WorkspaceDetailSheet.vue';
import WorkspaceDetailFrame from '@/components/workspace/WorkspaceDetailFrame.vue';
import ActionItemsDialog from '~/components/workspace/ActionItemsDialog.vue';
import SendFormDialog from '@/components/forms/SendFormDialog.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    AlertTriangle,
    ArrowLeft,
    BriefcaseBusiness,
    Check,
    ChevronDown,
    ChevronRight,
    Building2,
    CircleDot,
    ExternalLink,
    FileText,
    Info,
    Link2,
    Mail,
    MessageSquare,
    Phone,
    Plus,
    Receipt,
    Rocket,
    Sparkles,
    Send,
    ShieldCheck,
    SquareGanttChart,
    Trophy,
    Trash2,
    Upload,
    UserPlus2,
} from 'lucide-vue-next';
import { copyToClipboard } from '~/lib/clipboard';
import { clientShouldPromptOnWon } from '~/lib/clientLifecycleAutomation';
import {
    buildClientWorkspaceOwnershipScope,
    clientWorkspaceOwnsEntity,
    deriveClientWorkspaceOnboardingState,
    deriveDeliveryStageFromClientLifecycle,
    derivePipelineStageFromClientLifecycle,
    deriveClientCommercialStageFromClientLifecycle,
    resolveClientWorkspaceDetailContext,
} from '~/lib/clientWorkspaceDetail';
import { formatMoney, formatRelativeTime, formatShortDate } from '~/lib/formatters';
import { isInProgressPortalFormRequest } from '~/lib/portalFormProgress';
import { clientPortalRoleLabel } from '~/lib/clientPortalPermissions';
import { isPendingClientTaskWork } from '~/lib/taskClientResponse';
import { recommendClientOnboardingTemplateId } from '~/lib/clientOnboarding';
import { isImageUploadedFile } from '~/lib/uploadedFiles';
import { buildClientPortalLink, resolveTenantOrigin } from '~/lib/tenantLinks';
import { useClientWorkspaceLifecycle } from '~/composables/clients/useClientWorkspaceLifecycle';
import { useClientWorkspaceDelivery } from '~/composables/clients/useClientWorkspaceDelivery';
import { useClientWorkspacePeople } from '~/composables/clients/useClientWorkspacePeople';
import { useClientWorkspaceContract } from '~/composables/clients/useClientWorkspaceContract';
import { useClientWorkspaceDialogs } from '~/composables/clients/useClientWorkspaceDialogs';
import { useClientWorkspaceQuickLinks } from '~/composables/clients/useClientWorkspaceQuickLinks';
import { useClientWorkspaceMedia } from '~/composables/clients/useClientWorkspaceMedia';
import { useClientWorkspaceCommunications } from '~/composables/clients/useClientWorkspaceCommunications';
import { useClientWorkspaceActivity } from '~/composables/clients/useClientWorkspaceActivity';
import { useClientWorkspaceAutomation } from '~/composables/clients/useClientWorkspaceAutomation';
import { useClientWorkspaceEntities } from '~/composables/clients/useClientWorkspaceEntities';
import { useClientWorkspaceFinance } from '~/composables/clients/useClientWorkspaceFinance';
import { useClientWorkspaceOverviewSignals } from '~/composables/clients/useClientWorkspaceOverviewSignals';
import { useClientWorkspaceMobileMenu } from '~/composables/clients/useClientWorkspaceMobileMenu';
import { useClientWorkspaceAttentionActions } from '~/composables/clients/useClientWorkspaceAttentionActions';
import { usePortalFormProgressPolling } from '~/composables/forms/usePortalFormProgressPolling';
import {
    useClientWorkspaceOverview,
} from '~/composables/clients/useClientWorkspaceOverview';
import type { ClientTaskDraft } from '@/components/clients/delivery/types';
import type {
    Approval,
    Client,
    ClientLifecycle,
    ClientContact,
    FormSubmission,
    PortalFormRequest,
    Invoice,
    OnboardingRun,
    Project,
    Quote,
    ClientDeliveryStage,
    ClientPipelineStage,
    ClientCommercialStage,
    Task,
    UploadedFile,
} from '~/types/models';
import { toast } from 'vue-sonner';
import type { ClientNotificationTab } from '~/lib/notifications';

interface Props {
    clientSlug?: string;
    headerPresentation?: 'immersive' | 'contained';
}

const props = withDefaults(defineProps<Props>(), {
    clientSlug: '',
    headerPresentation: 'immersive',
});
const headerPresentation = computed(() => props.headerPresentation === 'contained' ? 'contained' : 'immersive');

const route = useRoute();
const runtimeConfig = useRuntimeConfig();
const requestUrl = useRequestURL();
const { tenantPath } = useTenantPath();
const { canMutate, canRespondApprovals } = usePermissions();
const tenantStore = useTenantStore();
const userStore = useUserStore();

const clientStore = useClientStore();
const projectStore = useProjectStore();
const onboardingStore = useOnboardingStore();
const quotesStore = useQuotesStore();
const proposalsStore = useProposalsStore();
const invoicesStore = useInvoicesStore();
const taskStore = useTaskStore();
const approvalsStore = useApprovalsStore();
const messagesStore = useMessagesStore();
const activityStore = useActivityStore();
const formStore = useFormStore();
const filesStore = useFilesStore();
const notificationStore = useNotificationStore();
const notificationRead = useNotificationReadAcknowledger();

const requestedClientSlug = computed(() => String(props.clientSlug || '').trim());
const loading = ref(true);
const loadError = ref('');
const resolvedClient = ref<Client | null>(null);
const linkedClient = computed<Client | null>(() => resolvedClient.value || null);
const clientProposals = computed(() => proposalsStore.proposals.filter(proposal => proposal.clientId === linkedClient.value?.id));
const {
    isSendFormOpen: isSendFormDialogOpen,
    isCreateQuoteOpen,
    isCreateInvoiceOpen,
    isOnboardingHandoffOpen,
    isOverviewNotesOpen: isOverviewNotesDialogOpen,
    isOverviewAttentionOpen: isOverviewAttentionDialogOpen,
    selectedOverviewAttentionCardId,
    isContactOpen: isContactDialogOpen,
    editingContact,
    isClientSettingsOpen: isEditClientDialogOpen,
    isQuickLinksOpen: isQuickLinksDialogOpen,
    selectedApproval,
    isApprovalOpen: isApprovalDialogOpen,
} = useClientWorkspaceDialogs();
const preparingQuoteClient = ref(false);
const clientDeleting = ref(false);
const notesDraft = ref('');
const notesSaving = ref(false);
const scopedFiles = ref<UploadedFile[]>([]);
const filesLoading = ref(false);
const workspaceTab = ref('overview');
const isPeopleSheetOpen = ref(false);
const isActivitySheetOpen = ref(false);
const isAutomationSheetOpen = ref(false);
const workspaceTabOptions = [
    { value: 'overview', label: 'Overview', icon: BriefcaseBusiness },
    { value: 'delivery', label: 'Work', icon: SquareGanttChart },
    { value: 'finance', label: 'Finance', icon: Receipt },
    { value: 'comms', label: 'Comms', icon: MessageSquare },
] as const;
const notificationMuteLoading = ref(false);
const clientNotificationContext = computed(() => ({
    surface: 'tenant' as const,
    tenantId: tenantStore.currentTenantId || null,
    clientId: linkedClient.value?.id || null,
}));
const clientTabNotificationCounts = computed<Record<string, number>>(() => Object.fromEntries(
    workspaceTabOptions.map((tab) => [
        tab.value,
        notificationStore.unreadCountForClientTab(tab.value as ClientNotificationTab, clientNotificationContext.value),
    ]),
));
const clientNotificationsMuted = computed(() => Boolean(
    tenantStore.currentTenantId
    && linkedClient.value?.id
    && notificationStore.isClientMuted(tenantStore.currentTenantId, linkedClient.value.id),
));

watch(workspaceTab, (tab) => {
    if (tab === 'people') {
        isPeopleSheetOpen.value = true;
        workspaceTab.value = 'overview';
        return;
    }
    if (tab === 'activity') {
        isActivitySheetOpen.value = true;
        workspaceTab.value = 'overview';
        return;
    }
    if (tab === 'automation') {
        isAutomationSheetOpen.value = true;
        workspaceTab.value = 'overview';
        return;
    }
    notificationRead.acknowledgeClientTab(tab as ClientNotificationTab, clientNotificationContext);
});
watch(isPeopleSheetOpen, (open) => {
    if (open) notificationRead.acknowledgeClientTab('people', clientNotificationContext.value);
});
watch(isActivitySheetOpen, (open) => {
    if (open) notificationRead.acknowledgeClientTab('activity', clientNotificationContext.value);
});
watch(isAutomationSheetOpen, (open) => {
    if (open) notificationRead.acknowledgeClientTab('automation', clientNotificationContext.value);
});

async function toggleClientNotifications() {
    const tenantId = tenantStore.currentTenantId;
    const clientId = linkedClient.value?.id;
    if (!tenantId || !clientId || notificationMuteLoading.value) return;

    notificationMuteLoading.value = true;
    try {
        const nextMuted = !clientNotificationsMuted.value;
        await notificationStore.setClientMuted(tenantId, clientId, nextMuted, clientTitle(linkedClient.value));
        toast.success(nextMuted ? 'Client notifications muted for you.' : 'Client notifications unmuted.');
    } catch (error: any) {
        toast.error(error?.message || 'Could not update client notifications.');
    } finally {
        notificationMuteLoading.value = false;
    }
}

async function loadClientWorkspaceData(currentClient: Client | null) {
    const scope = buildClientWorkspaceOwnershipScope({
        clientId: currentClient?.id,
        linkedProjectIds: projectStore.projects
            .filter((project) => normalizeText(project.clientId) === normalizeText(currentClient?.id))
            .map((project) => project.id),
    });
    const relatedProjectIds = projectStore.projects
        .filter((project) => clientWorkspaceOwnsEntity({
            scope,
            entityClientId: project.clientId,
            entityProjectId: project.id,
        }))
        .map((project) => project.id);

    filesLoading.value = true;
    const [filesResult] = await Promise.allSettled([
        filesStore.fetchFilesForScope(fileScopeEntities.value),
        currentClient?.id
            ? refreshPortalAccess({ sync: false })
            : Promise.resolve(),
        approvalsStore.fetchApprovalsForProjects(relatedProjectIds),
    ]);

    if (filesResult.status === 'fulfilled') {
        scopedFiles.value = filesResult.value;
    } else {
        console.warn('Failed to load scoped workspace files:', filesResult.reason);
        scopedFiles.value = [];
    }

    if (currentClient?.id) {
        await loadClientCommunications();
        return;
    }

    clientActivities.value = [];
    communicationsError.value = '';
}

onMounted(async () => {
    const requestedTab = String(route.query.tab || '').trim();
    if (requestedTab) {
        isPeopleSheetOpen.value = ['people', 'contacts'].includes(requestedTab);
        isActivitySheetOpen.value = ['activity', 'comments'].includes(requestedTab);
        isAutomationSheetOpen.value = requestedTab === 'automation';
        workspaceTab.value = mapLegacyClientTabToWorkspaceTab(requestedTab);
    }

    const routeClientSlug = requestedClientSlug.value;
    if (!routeClientSlug) {
        loadError.value = 'Client not found.';
        loading.value = false;
        return;
    }

    try {
        await Promise.all([
            clientStore.fetchClients(),
            projectStore.fetchProjects(),
            onboardingStore.fetchRuns(),
            onboardingStore.fetchTemplates(),
            quotesStore.fetchAll(),
            proposalsStore.fetchAll(),
            invoicesStore.fetchAll(),
            taskStore.fetchTasks(),
            messagesStore.fetchThreadSummaries(),
            formStore.fetchAll(),
        ]);

        let loadedClient: Client | null = null;
        if (routeClientSlug) {
            loadedClient = await clientStore.fetchClientBySlug(routeClientSlug);
        }

        if (!loadedClient) {
            loadError.value = 'Client not found.';
            return;
        }

        resolvedClient.value = loadedClient;

        if (String(route.query.contact || '').trim() === 'new') {
            isPeopleSheetOpen.value = true;
            openAddContactDialog();
        }

        if (String(route.query.sendForm || '').trim() === '1') {
            workspaceTab.value = 'overview';
            isSendFormDialogOpen.value = true;
        }

        await loadClientWorkspaceData(loadedClient);
        notificationRead.acknowledgeClientTab(workspaceTab.value as ClientNotificationTab, clientNotificationContext);
    } catch (error: any) {
        console.error('Failed to load client workspace detail:', error);
        loadError.value = error?.message || 'Unable to load client detail.';
    } finally {
        filesLoading.value = false;
        portalAccessLoading.value = false;
        loading.value = false;
    }
});
const detailContext = computed(() => resolveClientWorkspaceDetailContext({
    client: linkedClient.value,
    routeClientSlug: requestedClientSlug.value,
}));
const clientCommercialStage = computed(() => deriveClientCommercialStageFromClientLifecycle(linkedClient.value?.lifecycle));
const clientPipelineStage = computed(() => derivePipelineStageFromClientLifecycle(linkedClient.value?.lifecycle));
const clientDeliveryStage = computed(() => deriveDeliveryStageFromClientLifecycle(linkedClient.value?.lifecycle));
const {
    ownershipScope,
    relatedProjects,
    relatedProjectIds,
    relatedTasks,
    relatedRuns,
    relatedQuotes,
    relatedInvoices,
    relatedApprovals,
    relatedSubmissions,
    pendingTasks,
    pendingApprovals,
    outstandingInvoices,
    activeOnboardingRuns: onboardingActive,
    sentClientQuotes,
} = useClientWorkspaceEntities({
    client: linkedClient,
    projectStore,
    taskStore,
    onboardingStore,
    quotesStore,
    invoicesStore,
    approvalsStore,
    formStore,
});
const sendableForms = computed(() => formStore.forms.filter((form) => form.status === 'published'));
const relatedPortalFormRequests = computed<PortalFormRequest[]>(() => {
    const clientId = normalizeText(linkedClient.value?.id);
    if (!clientId) return [];

    return formStore.portalRequests.filter((request) => (
        normalizeText(request.clientId) === clientId
        && isInProgressPortalFormRequest(request)
    ));
});

usePortalFormProgressPolling(() => {
    if (!tenantStore.currentTenantId || !linkedClient.value?.id) return;
    return formStore.fetchPortalRequests({ silent: true });
});

const fileScopeEntities = computed(() => {
    if (!linkedClient.value?.id) return [] as Array<{ entityType: 'client' | 'project' | 'task', entityId: string }>;
    const linkedClientId = normalizeText(linkedClient.value.id);
    const projectIds = relatedProjectIds.value;

    return [
        ...(linkedClientId ? [{ entityType: 'client' as const, entityId: linkedClientId }] : []),
        ...projectIds.map((projectId) => ({ entityType: 'project' as const, entityId: projectId })),
        ...taskStore.tasks
            .filter((task) => clientWorkspaceOwnsEntity({
                scope: ownershipScope.value,
                entityClientId: task.clientId,
                entityProjectId: task.projectId,
            }))
            .slice(0, 12)
            .map((task) => ({ entityType: 'task' as const, entityId: task.id })),
    ];
});

const pipelineStageLabels: Record<ClientPipelineStage, string> = {
    new_enquiry: 'New enquiry',
    contacted: 'Contacted',
    qualified: 'Qualified',
    proposal_sent: 'Proposal sent',
    negotiation: 'Negotiation',
    won: 'Won',
    lost: 'Lost',
};

const commercialStageLabels: Record<ClientCommercialStage, string> = {
    lead: 'Lead',
    opportunity: 'Opportunity',
    client: 'Client',
    past_client: 'Past client',
    advocate: 'Advocate',
};

const deliveryStageLabels: Record<ClientDeliveryStage, string> = {
    pre_engagement: 'Pre-engagement',
    onboarding: 'Onboarding',
    active: 'Active',
    paused: 'Paused',
    complete: 'Complete',
};


function normalizeText(value: unknown): string {
    return String(value || '').trim();
}

async function refreshResolvedClient() {
    if (!linkedClient.value?.id) return;
    const refreshedClient = await clientStore.fetchClientById(linkedClient.value.id);
    if (refreshedClient) {
        resolvedClient.value = refreshedClient;
    }
}

async function refreshClientWorkspace() {
    await refreshResolvedClient();
}

async function updateClientLifecycle(patch: Partial<ClientLifecycle>) {
    if (!linkedClient.value?.id) return;
    const existingLifecycle = linkedClient.value.lifecycle || {};
    await clientStore.updateClient(linkedClient.value.id, {
        lifecycle: {
            ...existingLifecycle,
            ...patch,
        },
    });
    await refreshResolvedClient();
}

const {
    drafts: quickLinkDrafts,
    saving: quickLinksSaving,
    links: linkedClientQuickLinks,
    open: openQuickLinksDialog,
    add: addQuickLinkDraft,
    remove: removeQuickLinkDraft,
    save: saveQuickLinks,
} = useClientWorkspaceQuickLinks({
    client: linkedClient,
    dialogOpen: isQuickLinksDialogOpen,
    clientStore,
    refreshClient: refreshResolvedClient,
});

function externalHref(value: string): string {
    const url = String(value || '').trim();
    if (!url) return '#';
    if (/^(https?:|mailto:|tel:)/i.test(url)) return url;
    return `https://${url}`;
}

function mapLegacyClientTabToWorkspaceTab(tab: string): string {
    if (['overview', 'delivery', 'finance', 'comms'].includes(tab)) {
        return tab;
    }

    if (tab === 'contacts') {
        return 'overview';
    }

    if (['projects', 'tickets', 'forms', 'approvals', 'contract', 'uploads'].includes(tab)) {
        return 'delivery';
    }

    if (tab === 'invoices') {
        return 'finance';
    }

    if (['activity', 'comments'].includes(tab)) {
        return 'overview';
    }

    return 'overview';
}

function clientTitle(value: Client | null): string {
    if (!value) return 'Client';
    return value.companyName || value.businessName || value.legalName || value.name || 'Untitled client';
}

function clientTypeSummary(value: Client | null): string {
    if (!value) return 'Client account';
    return value.entityType === 'organization' || value.companyName || value.businessName
        ? 'Organization account'
        : 'Individual account';
}

function clientSourceSummary(value: Client | null): string {
    if (!value) return 'Created in Vuey';
    const createdLabel = formatShortDate(value.createdAt || value.updatedAt || new Date());
    const source = normalizeText(value.lifecycle?.source).toLowerCase();
    if (!source || source === 'manual') return `Created manually ${createdLabel}`;
    if (source === 'form_submission') return `Created from an enquiry ${createdLabel}`;
    return `Created from ${source.replace(/_/g, ' ')} ${createdLabel}`;
}

function clientContactSummary(value: Client | null): string {
    if (!value) return 'Add a primary email so the team has one clear route in.';
    if (value.email && value.phone) return 'Primary contact channels are in place for commercial and delivery follow-up.';
    if (value.email) return 'Email is ready to use; add a phone number if calls are part of the client rhythm.';
    if (value.phone) return 'Phone is ready to use; add an email so forms, portal invites, and updates have a home.';
    return 'No primary contact route is set yet. Add an email or phone so this account is easier to work.';
}

const {
    clientActivities,
    clientTouchpoints,
    threadSummary,
    timeline: communicationTimeline,
    loading: communicationsLoading,
    error: communicationsError,
    saving: touchpointSaving,
    draft: touchpointDraft,
    load: loadClientCommunications,
    log: logClientTouchpoint,
    channelLabel: communicationChannelLabel,
    directionLabel: communicationDirectionLabel,
    channelIcon: communicationChannelIcon,
    channelBadgeClass: communicationChannelBadgeClass,
} = useClientWorkspaceCommunications({
    client: linkedClient,
    activityStore,
    messagesStore,
    userStore,
    updateLifecycle: updateClientLifecycle,
    refreshClient: refreshResolvedClient,
    formatRelativeTime,
});

const {
    startingOnboarding,
    highlightedRun,
    onboardingSkipped,
    currentRunProject,
    recommendedOnboardingTemplate,
    readyToStartOnboarding,
    clientHeaderStatusLabel,
    daysInOnboarding,
    handleSkipOnboarding,
    handleStartOnboarding,
} = useClientWorkspaceLifecycle({
    client: linkedClient,
    relatedProjects,
    relatedRuns,
    onboardingActive,
    onboardingTemplates: computed(() => onboardingStore.templates),
    canMutate,
    userStore,
    clientStore,
    projectStore,
    onboardingStore,
    onboardingHandoffOpen: isOnboardingHandoffOpen,
    updateClientLifecycle,
    refreshResolvedClient,
    tenantPath,
    projectHref,
    pipelineStageLabels,
    shouldPromptOnWon: clientShouldPromptOnWon,
});
const galleryFilesAcrossProjects = computed(() => scopedFiles.value
    .filter((file) => isImageUploadedFile(file)));
const deliveryUploadScopeOptions = computed(() => {
    const options: Array<{ value: string, label: string }> = [];
    if (linkedClient.value?.id) {
        options.push({ value: `client:${linkedClient.value.id}`, label: 'Client workspace files' });
    }
    for (const project of relatedProjects.value) {
        if (project.id) {
            options.push({ value: `project:${project.id}`, label: `${project.name || 'Untitled project'} gallery / files` });
        }
    }
    return options;
});
const {
    isPublishProjectDialogOpen,
    isNoticeProjectDialogOpen,
    publishTargetProject,
    noticeTargetProject,
    selectedTaskId,
    isTaskDetailOpen,
    selectedDeliveryFiles,
    deliveryUploadScope,
    deliveryUploading,
    deliveryUploadError,
    openPublishProjectDialog,
    openProjectNoticeDialog,
    archiveRelatedProject,
    deleteRelatedProject,
    handleDeliveryFilesSelected,
    refreshScopedFiles,
    uploadDeliveryFiles,
    openTaskDetail,
    handleTaskUpdated,
    handleTaskDeleted,
    handleTaskCreated,
} = useClientWorkspaceDelivery({
    client: linkedClient,
    canMutate,
    relatedProjects,
    fileScopeEntities,
    uploadScopeOptions: deliveryUploadScopeOptions,
    projectStore,
    filesStore,
    taskStore,
    userStore,
    onFilesLoaded: (files) => { scopedFiles.value = files; },
    filesLoading,
});

function openProjectSettings(project: Project) {
    void navigateTo(tenantPath(projectSettingsPath(project.slug || project.id)));
}
const portalOrigin = computed(() => resolveTenantOrigin({
    tenant: tenantStore.currentTenant,
    appDomain: String(runtimeConfig.public.appDomain || ''),
    fallbackOrigin: requestUrl.origin,
}));
const clientPortalShareLink = computed(() => {
    if (!linkedClient.value?.slug) return '';
    return buildClientPortalLink({
        origin: portalOrigin.value,
        tenantId: tenantStore.currentTenantId || undefined,
        clientSlug: linkedClient.value.slug,
    });
});
const projectLinkItems = computed(() => relatedProjects.value.map((project) => ({
    project,
    href: projectHref(project),
})));
const {
    isClientContractDialogOpen,
    clientContractTitle,
    clientContractIntroMessage,
    clientContractBodyText,
    clientContractDocumentHtml,
    clientContractFields,
    clientContractTables,
    clientContractValues,
    clientContractProposalReferences,
    clientContractProposalReferenceLinks,
    clientContractTableValues,
    clientContractTemplateId,
    clientContractTenantSignatureName,
    clientContractDeliveryMethod,
    deliveryRecipient: clientContractDeliveryRecipient,
    clientContractContentMode,
    linkedClientContractFileId,
    selectedClientContractUpload,
    clientContractSaving,
    clientContractSending,
    clientContractUploading,
    clientContractPdfDownloading,
    clientContract,
    clientContractFiles,
    linkedClientContractFile,
    hasSelectedClientContractContent,
    clientContracts,
    contractStatusMeta,
    openClientContractDialog,
    openCreateContractDialog,
    saveClientContractDraft,
    sendClientContract,
    onClientContractFileSelected,
    resetClientContractDraft,
    formatContractSigner,
    downloadClientContractPdf,
} = useClientWorkspaceContract({
    canMutate,
    client: linkedClient,
    scopedFiles,
    filesStore,
    clientStore,
    tenantStore,
    runtimeConfig,
    requestUrl,
    relatedProjects,
    projectContractHref,
    refreshResolvedClient,
});

const {
    avatarInput: clientAvatarInput,
    headerInput: clientHeaderInput,
    savingTarget: clientImageSavingTarget,
    headerImage: clientHeaderImage,
    avatarImage: clientAvatarImage,
    openPicker: openClientImagePicker,
    handleSelected: handleClientImageSelected,
} = useClientWorkspaceMedia({
    client: linkedClient,
    relatedProjects,
    canMutate,
    clientStore,
    tenantStore,
    refreshClient: refreshResolvedClient,
});
const {
    currentYear,
    clientCurrency,
    clientOptions: financeClientOptions,
    projectOptions: financeProjectOptions,
    openQuotes,
    openInvoices,
    revenueInMotion,
    revenueCollectedThisYear,
    outstandingThisYear,
    clientWorthThisYear,
    wonRevenueThisYear,
} = useClientWorkspaceFinance({
    client: linkedClient,
    projects: relatedProjects,
    quotes: relatedQuotes,
    invoices: relatedInvoices,
});
const {
    portalAccessRecords,
    portalAccessLoading,
    portalAccessError,
    portalInviteLoading,
    portalActionKey,
    contactDeleting,
    clientContacts,
    primaryClientContact,
    hasPortalInviteableContact,
    acceptedPortalAccessCount,
    pendingPortalAccessCount,
    portalAccessByContact,
    portalContactRows,
    clientDigestRecipientEmail,
    clientDigestRecipientName,
    clientDigestHighlights,
    initialLetters,
    contactProjectAccessSummary,
    contactProjectAccessNames,
    contactPortalRoleSummary,
    openAddContactDialog,
    openEditContactDialog,
    handleSaveContact,
    handleDeleteContact,
    refreshPortalAccess,
    handleInviteToPortal,
    handleProvisionPortalAccess,
    handleInviteContact,
    handleProvisionContact,
    handleResetContactPassword,
    handleRevokePortalAccess,
    handleViewAsClientPortal,
    handleCopyClientPortalLink,
    handleSendClientDigest,
} = useClientWorkspacePeople({
    client: linkedClient,
    relatedProjects,
    outstandingInvoices,
    sentClientQuotes,
    clientCurrency,
    clientPortalShareLink,
    canMutate,
    tenantStore,
    clientStore,
    isContactDialogOpen,
    editingContact,
    refreshResolvedClient,
    refreshClientWorkspace,
    normalizeText,
});

function openContactFromClientSettings() {
    isEditClientDialogOpen.value = false;
    openAddContactDialog();
}

function openEditContactFromClientSettings(contact: ClientContact) {
    isEditClientDialogOpen.value = false;
    openEditContactDialog(contact);
}

async function handleDeleteLinkedClient() {
    const client = linkedClient.value;
    if (!client?.id || !canMutate.value) return;

    clientDeleting.value = true;
    try {
        const currentAccessRecords = await clientStore.listClientPortalAccess({ clientId: client.id });
        const accessRecords = currentAccessRecords.filter(record => ['pending', 'accepted'].includes(record.status));
        for (const record of accessRecords) {
            await clientStore.revokeClientPortalAccess({ clientId: client.id }, record.invitationId);
        }

        await clientStore.deleteClient(client.id);
        isEditClientDialogOpen.value = false;
        toast.success(`${client.name || 'Client'} deleted.`);
        await navigateTo(tenantPath('/clients'));
    } catch (error: any) {
        toast.error(error?.message || 'Could not delete the client.');
    } finally {
        clientDeleting.value = false;
    }
}
const {
    revenueSaving,
    automationSaving,
    automationDraft,
    automationSettings,
    revenueTarget,
    revenueCurrency,
    saveRevenue: saveRevenueIntelligence,
    saveSettings: saveAutomationSettings,
} = useClientWorkspaceAutomation({
    client: linkedClient,
    clientCurrency,
    updateLifecycle: updateClientLifecycle,
});
const {
    lastContactedAt,
    daysSinceLastContact,
    clientEntityLabel,
    projectStatsById,
    overviewConversationItems,
    waitingClientTasks,
    overdueInvoicesCount,
    overviewConversationSummary,
    overviewHealthIndicators,
} = useClientWorkspaceOverviewSignals({
    client: linkedClient,
    communicationTimeline,
    threadSummary,
    projects: relatedProjects,
    tasks: relatedTasks,
    files: scopedFiles,
    approvals: relatedApprovals,
    pendingTasks,
    pendingApprovals,
    outstandingInvoices,
    notes: notesDraft,
    hasHighlightedOnboarding: computed(() => Boolean(highlightedRun.value)),
    needsAttention,
    nextMoveLabel,
    communicationChannelLabel,
});
const {
    commercialFollowUpMeta,
    overviewAttentionCards,
    overviewActiveProjects,
    overviewAttentionDialogState,
    relatedProjectNameById,
    formatHumanValue,
} = useClientWorkspaceOverview({
    highlightedRun,
    linkedClient,
    clientPipelineStage,
    readyToStartOnboarding,
    waitingClientTasks,
    pendingApprovals,
    daysInOnboarding,
    tenantPath,
    daysSinceLastContact,
    selectedOverviewAttentionCardId,
    projectLinkItems,
    relatedTasks,
    relatedApprovals,
    projectStatsById,
    relatedProjects,
    relatedRuns,
    clientActivities,
    communicationTimeline,
    currentRunProject,
    recommendedOnboardingTemplate,
    primaryClientContact,
    lifecycleStatusLabel,
    nextMoveLabel,
    relatedInvoices,
    outstandingInvoices,
    overdueInvoicesCount,
    clientCurrency,
    canRespondApprovals,
    formatDate,
    formatRelativeTime,
    formatMoney,
    projectHref,
    communicationChannelLabel,
    communicationChannelIcon,
    communicationChannelBadgeClass,
    isPendingClientTaskWork,
});
const {
    overviewItems: overviewActivityItems,
    activityItems: fullClientActivityItems,
} = useClientWorkspaceActivity({
    communicationTimeline,
    quotes: relatedQuotes,
    invoices: relatedInvoices,
    projects: relatedProjects,
    onboardingRuns: relatedRuns,
    activities: clientActivities,
    formatHumanValue,
    communicationChannelLabel,
    communicationChannelIcon,
    communicationChannelBadgeClass,
});
watch(
    () => linkedClient.value?.lifecycle?.notes,
    (value) => {
        notesDraft.value = String(value || '');
    },
    { immediate: true },
);

function formatDate(value?: unknown) {
    return formatShortDate(value, 'No date yet');
}

function projectHref(project: Project): string | null {
    if (project.slug) return tenantPath(`/clients/projects/${project.slug}`);
    return project.id ? tenantPath(`/projects/${project.id}`) : null;
}

function projectContractHref(project: Project): string | null {
    if (project.slug) return tenantPath(`/clients/projects/${project.slug}?tab=contract`);
    return project.id ? tenantPath(`/projects/${project.id}?tab=contract`) : null;
}

const { openProjectQuickCreate } = useProjectQuickCreate();

function openProjectCreation() {
    openProjectQuickCreate(linkedClient.value?.id || '');
}

function openApprovalResponse(approval: Approval) {
    selectedApproval.value = approval;
    isApprovalDialogOpen.value = true;
}

const {
    approvalSaving,
    openAttentionDialog: openOverviewAttentionDialog,
    handlePrimary: handleOverviewAttentionDialogPrimaryAction,
    handleSecondary: handleOverviewAttentionDialogSecondaryAction,
    handleApprovalRespond: handleOverviewAttentionDialogApprovalRespond,
    focusTab: focusClientTab,
    remindApproval: handleOverviewAttentionDialogApprovalRemind,
    saveApproval: handleSaveApprovalResponse,
} = useClientWorkspaceAttentionActions({
    dialogState: overviewAttentionDialogState,
    relatedApprovals,
    activeTab: workspaceTab,
    selectedCardId: selectedOverviewAttentionCardId,
    attentionDialogOpen: isOverviewAttentionDialogOpen,
    notesDialogOpen: isOverviewNotesDialogOpen,
    onboardingDialogOpen: isOnboardingHandoffOpen,
    clientSettingsOpen: isEditClientDialogOpen,
    approvalDialogOpen: isApprovalDialogOpen,
    selectedApproval,
    approvalsStore,
    openTask: openTaskDetail,
});

function nextMoveLabel(): string {
    const pipelineStage = derivePipelineStageFromClientLifecycle(linkedClient.value?.lifecycle);
    const deliveryStage = deriveDeliveryStageFromClientLifecycle(linkedClient.value?.lifecycle);
    if (!linkedClient.value) return 'Review client';
    if (onboardingSkipped.value) return 'Onboarding skipped';
    if (pipelineStage === 'won' && deliveryStage !== 'onboarding' && !highlightedRun.value) return 'Start onboarding handoff';
    if (deliveryStage === 'onboarding') return 'Complete onboarding steps';
    if (pipelineStage === 'proposal_sent') return 'Follow up on the proposal';
    if (pipelineStage === 'negotiation') return 'Close the deal';
    if (pipelineStage === 'qualified') return 'Shape the next commercial step';
    if (pipelineStage === 'contacted') return 'Continue qualification';
    if (pipelineStage === 'new_enquiry') return 'Qualify the enquiry';
    if (deliveryStage === 'active') return 'Support active delivery';
    return 'Review client';
}

function lifecycleStatusLabel(client: Client | null | undefined): string {
    return formatHumanValue(client?.lifecycle?.status || 'unknown');
}

function normalizeRichTextValue(value: string) {
    const html = String(value || '').trim();
    if (!html) return '';
    const plainText = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    return plainText ? html : '';
}

function needsAttention(): boolean {
    const deliveryStage = deriveDeliveryStageFromClientLifecycle(linkedClient.value?.lifecycle);
    const pipelineStage = derivePipelineStageFromClientLifecycle(linkedClient.value?.lifecycle);
    if (!linkedClient.value) return false;
    if (onboardingSkipped.value) return false;
    return deliveryStage === 'onboarding'
        || pipelineStage === 'negotiation'
        || readyToStartOnboarding.value
        || outstandingInvoices.value.some((invoice) => ['overdue'].includes(String(invoice.status || '').toLowerCase()));
}

async function saveNotes() {
    if (!linkedClient.value?.id) return;

    notesSaving.value = true;
    try {
        const normalizedNotes = normalizeRichTextValue(notesDraft.value);
        await updateClientLifecycle({
            notes: normalizedNotes || undefined,
        });
        toast.success('Client notes updated.');
    } catch (error: any) {
        toast.error(error?.message || 'Could not save notes.');
    } finally {
        notesSaving.value = false;
    }
}

async function handleEditLinkedClient(updatedData: any) {
    if (!linkedClient.value) return;

    await clientStore.updateClient(linkedClient.value.id, updatedData);

    await Promise.all([
        refreshResolvedClient(),
        refreshClientWorkspace(),
        refreshPortalAccess({ sync: false, showLoading: false }),
    ]);

    isEditClientDialogOpen.value = false;
    toast.success('Client details updated.');
}

const commercialActions = useCommercialDocumentActions({
    canMutate,
    refresh: () => Promise.all([quotesStore.fetchAll(), invoicesStore.fetchAll()]),
});

async function handleQuoteCreated() {
    isCreateQuoteOpen.value = false;
    await quotesStore.fetchAll();
}

async function openCreateQuoteDialog() {
    if (!linkedClient.value) return;

    preparingQuoteClient.value = true;
    try {
        if (!linkedClient.value?.id) {
            throw new Error('Client not found.');
        }
        isCreateQuoteOpen.value = true;
    } catch (error: any) {
        toast.error(error?.message || 'Could not prepare this client for the quote.');
    } finally {
        preparingQuoteClient.value = false;
    }
}

async function handleInvoiceCreated() {
    isCreateInvoiceOpen.value = false;
    await invoicesStore.fetchAll();
}

const stats = computed(() => ({
    projects: relatedProjects.value.length,
    tasks: pendingTasks.value.length,
    approvals: pendingApprovals.value.length,
    invoices: outstandingInvoices.value.length,
}));
const clientOverviewBriefing = computed(() => {
    if (overviewAttentionCards.value.length === 0) return '';
    const titles = overviewAttentionCards.value.slice(0, 2).map(item => item.title.toLowerCase());
    return `${overviewAttentionCards.value.length} item${overviewAttentionCards.value.length === 1 ? '' : 's'} need attention: ${titles.join(' and ')}.`;
});

useClientWorkspaceMobileMenu({
    client: linkedClient,
    activeTab: workspaceTab,
    tabOptions: workspaceTabOptions,
    tabNotificationCounts: clientTabNotificationCounts,
    notificationsMuted: clientNotificationsMuted,
    quickLinks: linkedClientQuickLinks,
    canMutate,
    readyToStartOnboarding,
    portalLink: clientPortalShareLink,
    sendableFormCount: computed(() => sendableForms.value.length),
    tenantPath,
    externalHref,
    addProject: openProjectCreation,
    startOnboarding: () => { isOnboardingHandoffOpen.value = true; },
    skipOnboarding: handleSkipOnboarding,
    manageQuickLinks: openQuickLinksDialog,
    sendDigest: handleSendClientDigest,
    viewPortal: handleViewAsClientPortal,
    copyPortalLink: handleCopyClientPortalLink,
    sendForm: () => { isSendFormDialogOpen.value = true; },
    toggleNotifications: toggleClientNotifications,
});
</script>

<template>
    <WorkspaceDetailFrame
        :presentation="headerPresentation"
        :background-image-url="clientHeaderImage"
        :background-alt="linkedClient ? `${clientTitle(linkedClient)} cover` : ''"
        :background-fallback-style="{ backgroundColor: 'color-mix(in srgb, var(--primary) 24%, var(--background))' }"
    >
        <div v-if="loading" class="mx-4 min-h-40 px-4 py-6 text-muted-foreground sm:mx-5 lg:mx-6">
            Loading client...
        </div>

        <div v-else-if="loadError || !linkedClient" class="mx-4 min-h-40 px-4 py-6 text-destructive sm:mx-5 lg:mx-6">
            {{ loadError || 'Client not found.' }}
        </div>

        <template v-else>
            <ClientWorkspaceHero
                :presentation="headerPresentation"
                :title="clientTitle(linkedClient)"
                :header-image="clientHeaderImage"
                :avatar-image="clientAvatarImage"
                :avatar-fallback="initialLetters(clientTitle(linkedClient))"
                :entity-label="clientEntityLabel"
                :commercial-stage-label="commercialStageLabels[clientCommercialStage]"
                :delivery-stage-label="deliveryStageLabels[clientDeliveryStage]"
                :last-contact-label="lastContactedAt ? `Last contact ${formatRelativeTime(lastContactedAt, 'recently')}` : 'No recent contact logged'"
                :project-count="stats.projects"
                :status-label="clientHeaderStatusLabel"
                :quick-links="linkedClientQuickLinks"
                :inbox-href="tenantPath('/messages')"
                :clients-href="tenantPath('/clients')"
                :can-mutate="canMutate"
                :ready-to-start-onboarding="readyToStartOnboarding"
                :starting-onboarding="startingOnboarding"
                :header-saving="clientImageSavingTarget === 'header'"
                :avatar-saving="clientImageSavingTarget === 'avatar'"
                :portal-link-available="Boolean(clientPortalShareLink)"
                :can-send-form="sendableForms.length > 0"
                :notifications-muted="clientNotificationsMuted"
                :notification-mute-loading="notificationMuteLoading"
                @edit-image="openClientImagePicker"
                @manage-quick-links="openQuickLinksDialog"
                @add-project="openProjectCreation"
                @start-onboarding="isOnboardingHandoffOpen = true"
                @skip-onboarding="handleSkipOnboarding"
                @edit-client="isEditClientDialogOpen = true"
                @send-digest="handleSendClientDigest"
                @view-portal="handleViewAsClientPortal"
                @copy-portal-link="handleCopyClientPortalLink"
                @send-form="isSendFormDialogOpen = true"
                @toggle-notifications="toggleClientNotifications"
                @manage-people="isPeopleSheetOpen = true"
                @manage-automation="isAutomationSheetOpen = true"
                @open-activity="isActivitySheetOpen = true"
            />

            <input
                ref="clientAvatarInput"
                type="file"
                accept="image/*"
                class="hidden"
                @change="handleClientImageSelected($event, 'avatar')"
            >
            <input
                ref="clientHeaderInput"
                type="file"
                accept="image/*"
                class="hidden"
                @change="handleClientImageSelected($event, 'header')"
            >

            <Dialog v-if="canMutate && sendableForms.length > 0" v-model:open="isSendFormDialogOpen">
                <DialogContent class="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Send a form</DialogTitle>
                        <DialogDescription>Share a published form and keep the response attached to this client automatically.</DialogDescription>
                    </DialogHeader>
                    <SendFormDialog
                        :forms="sendableForms"
                        :tenant-id="String(tenantStore.currentTenantId || '')"
                        :target-label="clientTitle(linkedClient)"
                        :portal-client-slug="linkedClient?.slug"
                        :linked-client-id="linkedClient?.id"
                        :project-options="relatedProjects.map((project) => ({ id: project.id, name: project.name }))"
                    />
                </DialogContent>
            </Dialog>

            <div :class="headerPresentation === 'immersive' ? 'px-4 sm:px-5 lg:px-6' : 'pt-4'">
            <Tabs v-model="workspaceTab" class="relative z-10 space-y-4" :class="headerPresentation === 'immersive' ? '-mt-20 sm:-mt-24 lg:-mt-28' : ''">
                <TabsList class="h-auto w-full flex-wrap justify-start gap-2 rounded-xl bg-muted/65 p-1">
                    <TabsTrigger value="overview">
                        Overview
                        <span v-if="clientTabNotificationCounts.overview" class="tab-count-badge">{{ clientTabNotificationCounts.overview }}</span>
                    </TabsTrigger>
                    <TabsTrigger value="delivery">
                        Work
                        <span v-if="clientTabNotificationCounts.delivery" class="tab-count-badge">{{ clientTabNotificationCounts.delivery }}</span>
                    </TabsTrigger>
                    <TabsTrigger value="finance">
                        Finance
                        <span v-if="clientTabNotificationCounts.finance" class="tab-count-badge">{{ clientTabNotificationCounts.finance }}</span>
                    </TabsTrigger>
                    <TabsTrigger value="comms">
                        Comms
                        <span v-if="clientTabNotificationCounts.comms" class="tab-count-badge">{{ clientTabNotificationCounts.comms }}</span>
                    </TabsTrigger>
                </TabsList>

<TabsContent value="overview" class="mt-0 space-y-8">
                    <ClientOverviewTab
                        :can-mutate="canMutate"
                        :overview-attention-cards="overviewAttentionCards"
                        :overview-briefing="clientOverviewBriefing"
                        :overview-active-projects="overviewActiveProjects"
                        :notes="notesDraft"
                        :notes-open="isOverviewNotesDialogOpen"
                        :notes-saving="notesSaving"
                        :overview-conversation-items="overviewConversationItems"
                        :overview-conversation-summary="overviewConversationSummary"
                        :overview-health-indicators="overviewHealthIndicators"
                        :overview-activity-items="overviewActivityItems"
                        :project-count="stats.projects"
                        :open-task-count="stats.tasks"
                        :pending-approval-count="stats.approvals"
                        :outstanding-invoice-count="stats.invoices"
                        :communication-channel-icon="communicationChannelIcon"
                        :communication-channel-label="communicationChannelLabel"
                        @open-attention-card="openOverviewAttentionDialog"
                        @create-project="openProjectCreation"
                        @open-work="focusClientTab('delivery')"
                        @open-approvals="openOverviewAttentionDialog('pending-approvals')"
                        @open-finance="focusClientTab('finance')"
                        @update:notes="notesDraft = $event"
                        @update:notes-open="isOverviewNotesDialogOpen = $event"
                        @save-notes="saveNotes"
                        @open-comms="focusClientTab('comms')"
                        @open-activity="isActivitySheetOpen = true"
                    />
                    <ActionItemsDialog
                        :open="isOverviewAttentionDialogOpen"
                        :title="overviewAttentionDialogState?.title || 'Client actions'"
                        :description="overviewAttentionDialogState?.description || 'Review the actions connected to this client signal.'"
                        :items="overviewAttentionDialogState?.items || []"
                        :empty-title="overviewAttentionDialogState?.emptyTitle || 'No actions right now'"
                        :empty-description="overviewAttentionDialogState?.emptyDescription || 'When work needs attention, it will surface here.'"
                        :approval-can-respond="canRespondApprovals"
                        :approval-can-remind="canMutate"
                        @update:open="isOverviewAttentionDialogOpen = $event"
                        @primary-action="handleOverviewAttentionDialogPrimaryAction"
                        @secondary-action="handleOverviewAttentionDialogSecondaryAction"
                        @approval-respond="handleOverviewAttentionDialogApprovalRespond"
                        @approval-remind="handleOverviewAttentionDialogApprovalRemind"
                    />
                </TabsContent>

                <WorkspaceDetailSheet
                    v-model:open="isPeopleSheetOpen"
                    title="People and portal access"
                    description="Manage contacts, portal permissions and the people connected to this client."
                    width-class="sm:!max-w-5xl"
                >
                    <ClientPeopleTab
                        :can-mutate="canMutate"
                        :contact-count="clientContacts.length"
                        :accepted-portal-access-count="acceptedPortalAccessCount"
                        :pending-portal-access-count="pendingPortalAccessCount"
                        :primary-contact="primaryClientContact"
                        :client-email="linkedClient?.email"
                        :client-phone="linkedClient?.phone || linkedClient?.contactNumber"
                        :client-portal-share-link="clientPortalShareLink"
                        :has-portal-inviteable-contact="hasPortalInviteableContact"
                        :portal-invite-loading="portalInviteLoading"
                        :portal-action-key="portalActionKey"
                        :portal-access-error="portalAccessError"
                        :portal-contact-rows="portalContactRows"
                        :conversation-items="overviewConversationItems"
                        :thread-summary="threadSummary"
                        :messages-href="tenantPath('/messages')"
                        :contact-portal-role-summary="contactPortalRoleSummary"
                        :contact-project-access-summary="contactProjectAccessSummary"
                        :contact-project-access-names="contactProjectAccessNames"
                        :initial-letters="initialLetters"
                        :communication-channel-icon="communicationChannelIcon"
                        :communication-channel-label="communicationChannelLabel"
                        :communication-channel-badge-class="communicationChannelBadgeClass"
                        :format-relative-time="formatRelativeTime"
                        @add-contact="openAddContactDialog"
                        @invite-to-portal="handleInviteToPortal"
                        @provision-to-portal="handleProvisionPortalAccess"
                        @view-as-portal="handleViewAsClientPortal"
                        @copy-portal-link="handleCopyClientPortalLink"
                        @edit-contact="openEditContactDialog"
                        @invite-contact="handleInviteContact"
                        @provision-contact="handleProvisionContact"
                        @reset-contact-password="handleResetContactPassword"
                        @revoke-access="handleRevokePortalAccess"
                    />
                </WorkspaceDetailSheet>

                <TabsContent value="delivery" class="mt-0 space-y-6">
                    <ClientDeliveryTab
                        :related-projects="relatedProjects"
                        :pending-tasks="pendingTasks"
                        :pending-approvals="pendingApprovals"
                        :client-contracts="clientContracts"
                        :client-proposals="clientProposals"
                        :scoped-files="scopedFiles"
                        :project-link-items="projectLinkItems"
                        :project-stats-by-id="projectStatsById"
                        :can-mutate="canMutate"
                        :tenant-slug="String(route.params.tenantSlug || '')"
                        :linked-client-id="linkedClient?.id"
                        :publish-project-open="isPublishProjectDialogOpen"
                        :notice-project-open="isNoticeProjectDialogOpen"
                        :publish-target-project="publishTargetProject"
                        :notice-target-project="noticeTargetProject"
                        :upload-scope="deliveryUploadScope"
                        :upload-scope-options="deliveryUploadScopeOptions"
                        :selected-files-count="selectedDeliveryFiles.length"
                        :delivery-uploading="deliveryUploading"
                        :delivery-upload-error="deliveryUploadError"
                        :gallery-files-across-projects="galleryFilesAcrossProjects"
                        :files-loading="filesLoading"
                        :tasks-href="tenantPath('/tasks')"
                        :approvals-href="tenantPath({ path: '/projects', query: { attention: 'approvals' } })"
                        :can-create-contract="Boolean(linkedClient)"
                        :client-contract="clientContract"
                        :related-project-name-by-id="relatedProjectNameById"
                        :create-task="handleTaskCreated"
                        @update:publish-project-open="isPublishProjectDialogOpen = $event; if (!$event) publishTargetProject = null"
                        @update:notice-project-open="isNoticeProjectDialogOpen = $event; if (!$event) noticeTargetProject = null"
                        @update:upload-scope="deliveryUploadScope = $event"
                        @files-selected="handleDeliveryFilesSelected"
                        @upload-files="uploadDeliveryFiles"
                        @refresh-files="refreshScopedFiles"
                        @edit-project="openProjectSettings"
                        @notice-project="openProjectNoticeDialog"
                        @publish-project="openPublishProjectDialog"
                        @archive-project="archiveRelatedProject"
                        @delete-project="deleteRelatedProject"
                        @publish-project-saved="publishTargetProject = null"
                        @notice-project-saved="noticeTargetProject = null"
                        @open-task="openTaskDetail"
                        @open-approval="openApprovalResponse"
                        @create-contract="openCreateContractDialog"
                        @manage-client-contract="openClientContractDialog"
                        @download-client-contract-pdf="downloadClientContractPdf"
                    />
                </TabsContent>

                <TabsContent value="finance" class="mt-0 space-y-6">
                    <ClientFinanceTab
                        :can-mutate="canMutate"
                        :linked-client="linkedClient"
                        :create-quote-open="isCreateQuoteOpen"
                        :create-invoice-open="isCreateInvoiceOpen"
                        :preparing-quote-client="preparingQuoteClient"
                        :finance-client-options="financeClientOptions"
                        :finance-project-options="financeProjectOptions"
                        :related-quotes="relatedQuotes"
                        :related-invoices="relatedInvoices"
                        :open-quotes="openQuotes"
                        :outstanding-invoices="outstandingInvoices"
                        :client-currency="clientCurrency"
                        :outstanding-this-year="outstandingThisYear"
                        :revenue-collected-this-year="revenueCollectedThisYear"
                        :current-year="currentYear"
                        :initial-revenue-target="revenueTarget"
                        :initial-revenue-currency="revenueCurrency"
                        :format-money="formatMoney"
                        :format-human-value="formatHumanValue"
                        :format-date="formatDate"
                        @update:create-quote-open="isCreateQuoteOpen = $event"
                        @update:create-invoice-open="isCreateInvoiceOpen = $event"
                        @open-create-quote="openCreateQuoteDialog"
                        @created-quote="handleQuoteCreated"
                        @created-invoice="handleInvoiceCreated"
                        @invoice-send="commercialActions.sendInvoice"
                        @invoice-remind="commercialActions.remindInvoice"
                        @invoice-mark-paid="commercialActions.markInvoicePaid"
                        @invoice-copy-public-link="commercialActions.copyInvoiceLink"
                        @quote-send="commercialActions.sendQuote"
                        @quote-remind="commercialActions.remindQuote"
                        @quote-accept="commercialActions.acceptQuote"
                        @quote-approve-internally="commercialActions.approveQuoteInternally"
                        @quote-reject="commercialActions.rejectQuote"
                        @quote-create-full-invoice="commercialActions.createFullInvoice"
                        @quote-create-deposit-invoice="commercialActions.createDepositInvoice"
                        @quote-create-balance-invoice="commercialActions.createBalanceInvoice"
                        @quote-create-next-installment-invoice="commercialActions.createNextInstallmentInvoice"
                        @quote-copy-public-link="commercialActions.copyQuoteLink"
                    />
                </TabsContent>

                <TabsContent value="comms" class="mt-0 space-y-6">
                    <ClientCommsTab
                        :client-id="linkedClient?.id || ''"
                        :client-name="linkedClient?.name || 'Client'"
                        :can-mutate="canMutate"
                        :related-projects="relatedProjects"
                        :touchpoint-count="clientTouchpoints.length"
                        :thread-summary="threadSummary"
                        :last-contacted-at="lastContactedAt"
                        :submission-count="relatedSubmissions.length"
                        :sendable-form-count="sendableForms.length"
                        :touchpoint-draft="touchpointDraft"
                        :touchpoint-saving="touchpointSaving"
                        :communications-loading="communicationsLoading"
                        :communications-error="communicationsError"
                        :communication-timeline="communicationTimeline"
                        :related-submissions="relatedSubmissions"
                        :related-form-requests="relatedPortalFormRequests"
                        :communication-channel-icon="communicationChannelIcon"
                        :communication-channel-label="communicationChannelLabel"
                        :communication-direction-label="communicationDirectionLabel"
                        :format-relative-time="formatRelativeTime"
                        :format-date="formatDate"
                        @log-touchpoint="logClientTouchpoint"
                        @message-sent="loadClientCommunications"
                    />
                </TabsContent>

                <WorkspaceActivitySheet
                    v-model:open="isActivitySheetOpen"
                    title="Client activity"
                    description="Communication, finance, onboarding and client changes in one timeline."
                    :items="fullClientActivityItems"
                    entity-type="client"
                    :entity-id="linkedClient?.id || ''"
                    comments-title="Client comments"
                    comments-placeholder="Add context, decisions, or handoff notes..."
                />

                <WorkspaceDetailSheet
                    v-if="linkedClient"
                    v-model:open="isAutomationSheetOpen"
                    title="Client automation"
                    description="Configure follow-through rules and revenue intelligence for this client."
                    width-class="sm:!max-w-5xl"
                >
                    <ClientAutomationTab
                        :client-worth-this-year="clientWorthThisYear"
                        :revenue-in-motion="revenueInMotion"
                        :revenue-collected-this-year="revenueCollectedThisYear"
                        :outstanding-this-year="outstandingThisYear"
                        :client-currency="clientCurrency"
                        :current-year="currentYear"
                        :initial-revenue-target="revenueTarget"
                        :initial-revenue-currency="revenueCurrency"
                        :automation-draft="automationDraft"
                        :automation-settings-reengagement-days="automationSettings.reengageAfterInactiveDays"
                        :revenue-saving="revenueSaving"
                        :automation-saving="automationSaving"
                        :format-money="formatMoney"
                        @save-revenue="saveRevenueIntelligence"
                        @save-settings="saveAutomationSettings"
                    />
                </WorkspaceDetailSheet>
            </Tabs>
            </div>
        </template>
    </WorkspaceDetailFrame>

    <ClientWorkspaceDialogs
        v-model:contact-open="isContactDialogOpen"
        v-model:edit-client-open="isEditClientDialogOpen"
        v-model:task-open="isTaskDetailOpen"
        v-model:approval-open="isApprovalDialogOpen"
        v-model:onboarding-open="isOnboardingHandoffOpen"
        :linked-client="linkedClient"
        :related-projects="relatedProjects"
        :editing-contact="editingContact"
        :selected-task-id="selectedTaskId"
        :selected-approval="selectedApproval"
        :approval-saving="approvalSaving"
        :starting-onboarding="startingOnboarding"
        :contact-deleting="contactDeleting"
        :client-deleting="clientDeleting"
        :can-delete-client="canMutate"
        :handle-save-contact="handleSaveContact"
        :handle-delete-contact="handleDeleteContact"
        :handle-add-contact="openContactFromClientSettings"
        :handle-edit-contact="openEditContactFromClientSettings"
        :handle-edit-linked-client="handleEditLinkedClient"
        :handle-delete-linked-client="handleDeleteLinkedClient"
        :handle-task-updated="handleTaskUpdated"
        :handle-task-deleted="handleTaskDeleted"
        :handle-save-approval-response="handleSaveApprovalResponse"
        :handle-start-onboarding="handleStartOnboarding"
        @update:contact-open="(value) => { isContactDialogOpen = value; if (!value) editingContact = null; }"
    />

    <ClientQuickLinksDialog
        :open="isQuickLinksDialogOpen"
        :drafts="quickLinkDrafts"
        :saving="quickLinksSaving"
        @update:open="isQuickLinksDialogOpen = $event"
        @update:drafts="quickLinkDrafts = $event"
        @add="addQuickLinkDraft"
        @remove="removeQuickLinkDraft"
        @save="saveQuickLinks"
    />

    <ClientContractDialogs
        :can-mutate="canMutate"
        :client-id="linkedClient?.id || ''"
        v-model:client-open="isClientContractDialogOpen"
        v-model:title="clientContractTitle"
        v-model:intro-message="clientContractIntroMessage"
        v-model:body-text="clientContractBodyText"
        v-model:document-html="clientContractDocumentHtml"
        v-model:fields="clientContractFields"
        v-model:tables="clientContractTables"
        v-model:values="clientContractValues"
        v-model:proposal-references="clientContractProposalReferences"
        v-model:proposal-reference-links="clientContractProposalReferenceLinks"
        v-model:table-values="clientContractTableValues"
        v-model:template-id="clientContractTemplateId"
        v-model:tenant-signature-name="clientContractTenantSignatureName"
        v-model:delivery-method="clientContractDeliveryMethod"
        :recipient-name="clientContractDeliveryRecipient.name"
        :recipient-email="clientContractDeliveryRecipient.email"
        v-model:content-mode="clientContractContentMode"
        v-model:file-id="linkedClientContractFileId"
        :client-contract="clientContract"
        :client-proposals="clientProposals"
        :client-proposals-loading="proposalsStore.loading"
        :client-contract-files="clientContractFiles"
        :linked-client-contract-file="linkedClientContractFile"
        :has-selected-client-contract-content="hasSelectedClientContractContent"
        :client-contract-pdf-downloading="clientContractPdfDownloading"
        :client-contract-saving="clientContractSaving"
        :client-contract-sending="clientContractSending"
        :client-contract-uploading="clientContractUploading"
        :on-client-contract-file-selected="onClientContractFileSelected"
        :download-client-contract-pdf="downloadClientContractPdf"
        :reset-client-contract-draft="resetClientContractDraft"
        :save-client-contract-draft="saveClientContractDraft"
        :send-client-contract="sendClientContract"
        :format-short-date="formatShortDate"
        :format-contract-signer="formatContractSigner"
    />
</template>
